import { useMemo } from "react";
import { AlertCircle, CreditCard, FileText, PenLine, Receipt, type LucideIcon } from "lucide-react";
import { Button, Card, cn } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import type { PortalPayable } from "../../../lib/portal";
import { fmtDate, fmtAmount } from "../../../lib/format";
import { relativeDay } from "../../../lib/due";
import type { AttentionItem } from "./attention";

const ICON: Record<AttentionItem["kind"], LucideIcon> = {
  invoice: Receipt,
  estimate: FileText,
  contract: PenLine,
};

/**
 * The list of things waiting for the reader, one row each, most pressing first.
 *
 * Every row says what the thing is, when it is due and how much it is for, and
 * ends in the one button that deals with it. Only the FIRST row's button is
 * solid: a column of identical solid buttons gives no steer, and the top of
 * this list is by construction the thing to do first.
 *
 * Late money is marked three ways at once - a tint, an alert glyph and the
 * words "is overdue" - so the row never depends on red alone.
 */
export function AttentionList({
  items,
  payable,
  payBusyId,
  onPay,
  onOpen,
}: {
  items: AttentionItem[];
  /** The server's word on which invoices can be paid by card (see `usePayable`). */
  payable: Record<string, PortalPayable>;
  payBusyId: string | null;
  onPay: (invoiceId: string) => void;
  onOpen: (item: AttentionItem) => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  // One clock for the whole list, so two rows cannot disagree about "today".
  const now = useMemo(() => new Date(), []);

  return (
    <Card className="overflow-hidden">
      <ul className="divide-y divide-border">
        {items.map((item, index) => {
          const late = item.kind === "invoice" && item.overdue;
          const Icon = late ? AlertCircle : ICON[item.kind];
          const row = describe(item, t, locale, now);
          const canPay = item.kind === "invoice" && payable[item.id]?.available === true;
          return (
            <li
              key={`${item.kind}-${item.id}`}
              className={cn("flex flex-wrap items-center gap-x-4 gap-y-3 px-5 py-4", late && "bg-destructive/5")}
            >
              <span
                aria-hidden="true"
                className={cn(
                  "flex size-10 shrink-0 items-center justify-center rounded-xl",
                  late ? "bg-destructive/15 text-destructive" : "bg-primary/10 text-primary",
                )}
              >
                <Icon className="size-5" />
              </span>
              <div className="min-w-0 flex-1 basis-52">
                <p className="font-semibold">{row.title}</p>
                {row.meta && <p className="mt-0.5 text-sm text-foreground/70">{row.meta}</p>}
              </div>
              <div className="flex w-full items-center justify-between gap-4 sm:w-auto sm:justify-end">
                <p className="font-display text-lg font-bold tabular-nums sm:w-28 sm:text-right">{row.amount}</p>
                <div className="flex sm:w-48 sm:justify-end">
                  {canPay ? (
                    <Button
                      variant={index === 0 ? "primary" : "outline"}
                      loading={payBusyId === item.id}
                      disabled={payBusyId !== null}
                      onClick={() => onPay(item.id)}
                      className="max-sm:h-11"
                    >
                      <CreditCard />
                      {t("portal.overview.todo.pay")}
                    </Button>
                  ) : (
                    <Button
                      variant={index === 0 ? "primary" : "outline"}
                      onClick={() => onOpen(item)}
                      className="max-sm:h-11"
                    >
                      {t(`portal.overview.todo.open.${item.kind}`)}
                    </Button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/** The three strings a row prints, for each kind of thing that can wait. */
function describe(
  item: AttentionItem,
  t: (key: string, vars?: Record<string, string | number>) => string,
  locale: string,
  now: Date,
): { title: string; meta: string; amount: string } {
  switch (item.kind) {
    case "invoice": {
      const due = item.dueDate
        ? t(item.overdue ? "portal.overview.todo.dueWas" : "portal.overview.todo.due", {
            date: fmtDate(item.dueDate, locale),
          })
        : null;
      return {
        title: t(item.overdue ? "portal.overview.todo.invoiceOverdue" : "portal.overview.todo.invoiceDue", {
          number: item.number,
        }),
        meta: [due, relativeDay(item.dueDate, locale, now)].filter(Boolean).join(" · "),
        amount: fmtAmount(item.amountCents, item.currency, locale),
      };
    }
    case "estimate":
      return {
        title: t("portal.overview.todo.estimate", { number: item.number }),
        meta: [
          item.title,
          item.validUntil ? t("portal.overview.todo.validUntil", { date: fmtDate(item.validUntil, locale) }) : null,
        ].filter(Boolean).join(" · "),
        amount: fmtAmount(item.amountCents, item.currency, locale),
      };
    case "contract":
      return {
        title: t("portal.overview.todo.contract", { title: item.title }),
        meta: [
          item.number,
          item.startDate || item.endDate ? `${fmtDate(item.startDate, locale)} - ${fmtDate(item.endDate, locale)}` : null,
        ].filter(Boolean).join(" · "),
        // A contract waits for a signature, not for money. The cell stays so
        // the buttons below and above it still line up.
        amount: "",
      };
  }
}
